import { Box, Container, Heading, SimpleGrid, Text, VStack, HStack, Tag } from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { usePublicHelpCategories } from '../hooks/useHelpCategories';

export default function HelpCenterLanding() {
  const [lang, setLang] = useState<'fr' | 'en'>('fr');
  const navigate = useNavigate();
  /**
   * `isError` is taken because a failed read of `GET help/categories` leaves
   * `data === undefined`, and this page then rendered NOTHING at all: the grid
   * maps over `categories?.map(...)` (no rows) while the empty-state line is
   * guarded by `categories?.length === 0`, which is `undefined === 0`, i.e.
   * false. A visitor got the hero and bare white space below it, with no way to
   * tell an outage from a help centre that has not been written yet.
   *
   * This route carries no guard at all (`@Get('categories')` in
   * `api/src/help-center/help-center.controller.ts`, read this run), so the
   * failure modes here are 5xx and network — and a help centre is the surface a
   * user reaches BECAUSE something else is already broken.
   */
  const { data: categories, isLoading, isError } = usePublicHelpCategories(lang);

  /**
   * `isError` alone is not "we could not read this": React Query keeps the last
   * good `data` when a BACKGROUND refetch fails, and painting an error over a
   * list that is on screen and correct is the mirror of the defect being fixed
   * here. The missing data is the defect; the flag is only how we learn of it.
   */
  const categoriesUnavailable = isError && !categories;

  return (
    <Container maxW="1000px" py={16}>
      {/* Language Toggle */}
      <HStack justify="flex-end" mb={6}>
        <Tag
          size="md"
          variant={lang === 'fr' ? 'solid' : 'outline'}
          colorScheme="brand"
          cursor="pointer"
          onClick={() => setLang('fr')}
        >
          FR
        </Tag>
        <Tag
          size="md"
          variant={lang === 'en' ? 'solid' : 'outline'}
          colorScheme="brand"
          cursor="pointer"
          onClick={() => setLang('en')}
        >
          EN
        </Tag>
      </HStack>

      {/* Hero */}
      <VStack textAlign="center" mb={12} spacing={3}>
        <Heading size="xl">
          {lang === 'fr' ? 'Centre d\'aide MySportSet' : 'MySportSet Help Center'}
        </Heading>
        <Text color="gray.600" fontSize="lg" maxW="600px">
          {lang === 'fr'
            ? 'Tout ce dont vous avez besoin pour g\u00e9rer votre club efficacement'
            : 'Everything you need to manage your venue efficiently'}
        </Text>
      </VStack>

      {/* Category Grid */}
      {categoriesUnavailable ? (
        <InlineErrorState
          message={
            lang === 'fr'
              ? 'Impossible de charger les rubriques d\u2019aide pour le moment. Veuillez r\u00e9essayer.'
              : 'We could not load the help topics right now. Please try again.'
          }
        />
      ) : isLoading ? (
        <Text color="gray.500" textAlign="center">Loading...</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={5}>
          {categories?.map((cat) => (
            <Box
              key={cat.id}
              p={6}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ borderColor: 'brand.300', shadow: 'md', transform: 'translateY(-2px)' }}
              onClick={() => navigate(`/marketing/help/${cat.slug}`)}
            >
              <Heading size="sm" mb={2}>
                {lang === 'en' ? cat.nameEn : cat.nameFr}
              </Heading>
              {(lang === 'en' ? cat.descriptionEn : cat.descriptionFr) && (
                <Text color="gray.500" fontSize="sm" mb={3}>
                  {lang === 'en' ? cat.descriptionEn : cat.descriptionFr}
                </Text>
              )}
              <Text color="gray.400" fontSize="xs">
                {cat.articleCount} article{cat.articleCount !== 1 ? 's' : ''}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* `!categoriesUnavailable` is redundant today (a failed read leaves `categories`
          undefined, so the length test is already false) and is written out
          anyway: it is the sentence that must stay true if anyone ever gives
          this read a `placeholderData` or a `?? []` default — the exact change
          that created this class in seventeen other components (D57). */}
      {!categoriesUnavailable && !isLoading && categories?.length === 0 && (
        <Text color="gray.500" textAlign="center" py={12}>
          {lang === 'fr' ? 'Aucun contenu pour le moment.' : 'No content yet.'}
        </Text>
      )}
    </Container>
  );
}
